// Paused screen shown to ordinary visitors when Commons is paused.
// Deliberately calm and minimal. Does not surface API failures.
//
// Self-heal (Feb 2026): `/paused` was previously a stateless leaf
// with no discovery mechanism, so a visitor who arrived here while
// the site was paused stayed here forever — even after the operator
// re-activated Commons. This effect fixes that cul-de-sac without
// touching the maintenance-page design, the backend pause gate, the
// interceptor, or the routing model.
//
//   - On mount, immediately ask `/admin/status` (never behind the
//     pause gate, always answerable, publishes `{paused:bool}`).
//     If `paused === false`, redirect to `/`.
//   - While the visitor stays here, re-check every ~15 seconds.
//     As soon as pause clears, redirect to `/`.
//   - Status request failures fail silently — no error overlay, no
//     false redirect — and the next interval retries.
//
// Copy and visual treatment are unchanged (see Feb 2026 note above).

import { useEffect } from "react";
import { adminStatus } from "../lib/api";

const RECHECK_MS = 15000;

export default function Paused() {
  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const s = await adminStatus();
        if (cancelled) return;
        if (s && s.paused === false) {
          window.location.replace("/");
        }
      } catch (_) {
        // Silent — network / transient server error. Try again on
        // the next interval; never redirect on failure.
      }
    };

    check();
    const id = setInterval(check, RECHECK_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <div className="c-paused-page" data-testid="paused-page">
      <div className="c-paused-shell">
        <div className="c-paused-mark" aria-hidden>🌱</div>
        <h1 className="c-paused-title">Commons is taking a quick break.</h1>
        <p className="c-paused-sub">
          We're doing a little maintenance. Come back soon.
        </p>
      </div>
    </div>
  );
}
